import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Layer, Line, Stage, Text } from 'react-konva';
import { formatTimestamp, sortArray } from '@nu-art/ts-common';
import { getEncounterEventTypeLabel } from '@app/ehr-emulator-shared';
import { ICONS } from '@app/styles';
import { Button, ComponentSync, LL_V_L, openContent } from '@nu-art/thunderstorm-frontend';
import { AXIS_COLOR, DEFAULT_CHART_WIDTH, ENCOUNTER_EVENT_TYPE_ICONS, ICON_DRAW_SIZE, LANE_STEP, MARGIN, MIN_CHART_WIDTH, STACKED_CHART_MARGIN, STACKED_LANE_STEP, MIN_DURATION_MS, MIN_MARKER_GAP_PX, STACKED_EMPTY_VIEWPORT_MIN_HEIGHT, TRACK_LINE_COLOR, TICK_COLOR, } from './consts.js';
import { formatElapsedSinceStart, getMarkerCenterPx } from './eventsTimelineHelpers.js';
import { ENCOUNTER_EVENT_MINUTE_MS, getEncounterEventTimelineMs, getEventDisplayLabel } from '../encounterEventUtils.js';
import './EventsTimeline.scss';
export class EventsTimeline extends ComponentSync {
    /** Distance from the track line (center) to the top of tick time labels. */
    static TICK_TIME_LABEL_Y_OFFSET = 10;
    static GAP_AFTER_TICK_TIME_LABELS = 6;
    timelineCache;
    timelineViewportRef = React.createRef();
    stageWrapRef = React.createRef();
    resizeObserver = null;
    _mounted = false;
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.barMenu ??= null;
        state.creatingSimulation ??= false;
        state.chartWidth ??= DEFAULT_CHART_WIDTH;
        const nextEncounterId = nextProps.group.encounterData._id ?? '';
        if (state.prevEncounterId !== nextEncounterId) {
            state.barMenu = null;
            state.selectedMarkerKey = undefined;
            this.timelineCache = undefined;
        }
        state.prevEncounterId = nextEncounterId;
        return state;
    }
    componentDidMount() {
        this._mounted = true;
        this.updateChartWidth();
        window.addEventListener('resize', this.updateChartWidth);
        this.attachTimelineViewportObserver();
    }
    componentWillUnmount() {
        this._mounted = false;
        window.removeEventListener('resize', this.updateChartWidth);
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }
    //######################### Logic #########################
    attachTimelineViewportObserver = () => {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const el = this.timelineViewportRef.current;
        if (!el) {
            return;
        }
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
            this.updateChartWidth();
        });
        this.resizeObserver.observe(el);
    };
    handleMarkerBubbleClick = (m, e) => {
        if (!this.props.onCreateSimulationForBar) {
            return;
        }
        e.stopPropagation();
        const wrap = this.stageWrapRef.current;
        if (!wrap) {
            return;
        }
        const r = wrap.getBoundingClientRect();
        this.setState({
            barMenu: {
                bucketStartMs: m.bucketStartMs,
                stageX: e.clientX - r.left,
                stageY: e.clientY - r.top,
                markerKey: m.key,
            },
        });
    };
    handleBarMenuCreateSimulation = async () => {
        const { barMenu } = this.state;
        const { onCreateSimulationForBar } = this.props;
        if (!barMenu || !onCreateSimulationForBar) {
            return;
        }
        this.setState({ creatingSimulation: true });
        try {
            const ok = await onCreateSimulationForBar(barMenu.bucketStartMs);
            if (!this._mounted)
                return;
            if (ok) {
                this.setState({ barMenu: null, creatingSimulation: false, selectedMarkerKey: barMenu.markerKey });
            }
            else {
                this.setState({ creatingSimulation: false });
            }
        }
        catch {
            if (!this._mounted)
                return;
            this.setState({ creatingSimulation: false });
        }
    };
    getRawEncounterSpan = (group) => {
        const encounterStart = group.encounterData.encounterStartTime ?? 0;
        let lastEventMs = encounterStart;
        for (const { event } of group.eventsWithSimulations) {
            lastEventMs = Math.max(lastEventMs, getEncounterEventTimelineMs(event));
        }
        const spanRaw = Math.max(MIN_DURATION_MS, lastEventMs - encounterStart);
        const spanMs = spanRaw * 1.08;
        return { encounterStart, spanMs };
    };
    getPlotLayout = (viewportWidth, margin) => {
        const stageViewport = Math.max(MIN_CHART_WIDTH, viewportWidth);
        const viewportInner = stageViewport - margin.left - margin.right;
        const plotWidthPx = viewportInner;
        const stageWidth = margin.left + plotWidthPx + margin.right;
        return { plotWidthPx, stageWidth };
    };
    /** Places the track so marker bubbles (above the line) clear the top by {@code marginTop}. */
    computeTrackY = (maxLane, marginTop, laneStep, tightMarkerPad) => {
        const markerTail = tightMarkerPad ? 2 : 4;
        const markerVisualHalf = ICON_DRAW_SIZE / 2 + 3 + markerTail;
        return marginTop + maxLane * laneStep + markerVisualHalf;
    };
    /** Space below the horizontal track: tick labels, then "Encounter start", then padding. */
    computeStageHeightFromTrack = (trackY, tickFontSize, encounterTitleFontSize, compact) => {
        const tickTimeLabelY = trackY + EventsTimeline.TICK_TIME_LABEL_Y_OFFSET;
        const encounterStartY = tickTimeLabelY + tickFontSize + EventsTimeline.GAP_AFTER_TICK_TIME_LABELS;
        const bottomPad = compact ? 4 : 14;
        return encounterStartY + encounterTitleFontSize + bottomPad;
    };
    buildTimelineMarkers = (group, plotWidthPx, spanMs) => {
        const rows = sortArray(group.eventsWithSimulations.map((ev, i) => {
            const event = ev.event;
            const timeMs = getEncounterEventTimelineMs(event);
            const bucketStartMs = Math.floor(timeMs / ENCOUNTER_EVENT_MINUTE_MS) * ENCOUNTER_EVENT_MINUTE_MS;
            const eventType = 'eventType' in event ? event.eventType : '';
            return {
                key: `${event._id ?? i}-${timeMs}`,
                timeMs,
                eventType,
                titleLine: getEventDisplayLabel(event),
                bucketStartMs,
            };
        }), (row) => row.timeMs);
        if (rows.length === 0) {
            return { markers: [], maxLane: 0 };
        }
        const clusterSpan = Math.max(spanMs, 1);
        const markers = [];
        const refW = Math.max(plotWidthPx, MIN_CHART_WIDTH);
        let i = 0;
        while (i < rows.length) {
            let j = i + 1;
            while (j < rows.length) {
                const deltaNorm = Math.abs(rows[j].timeMs - rows[i].timeMs) / clusterSpan;
                const estPx = deltaNorm * refW;
                if (estPx >= MIN_MARKER_GAP_PX) {
                    break;
                }
                j++;
            }
            const slice = rows.slice(i, j);
            slice.forEach((row, lane) => {
                markers.push({
                    key: row.key,
                    timeMs: row.timeMs,
                    eventType: row.eventType,
                    titleLine: row.titleLine,
                    bucketStartMs: row.bucketStartMs,
                    lane,
                });
            });
            i = j;
        }
        const maxLane = markers.reduce((m, x) => Math.max(m, x.lane), 0);
        return { markers, maxLane };
    };
    getTimelineMarkers = (group, plotWidthPx, viewportWidth) => {
        const { spanMs } = this.getRawEncounterSpan(group);
        const widthKey = Math.round(viewportWidth / 40) * 40;
        const viewKey = String(Math.round(plotWidthPx));
        if (this.timelineCache?.group === group &&
            this.timelineCache.widthKey === widthKey &&
            this.timelineCache.viewKey === viewKey) {
            return { markers: this.timelineCache.markers, maxLane: this.timelineCache.maxLane };
        }
        const built = this.buildTimelineMarkers(group, plotWidthPx, spanMs);
        this.timelineCache = { group, widthKey, viewKey, markers: built.markers, maxLane: built.maxLane };
        return built;
    };
    updateChartWidth = () => {
        const nextWidth = this.timelineViewportRef.current?.clientWidth ?? DEFAULT_CHART_WIDTH;
        this.setState((prevState) => (prevState.chartWidth === nextWidth ? null : { chartWidth: nextWidth }));
    };
    getTimelineRenderContext = () => {
        const { group, onCreateSimulationForBar } = this.props;
        const { barMenu, creatingSimulation, chartWidth = DEFAULT_CHART_WIDTH, selectedMarkerKey } = this.state;
        const viewportWidth = Math.max(MIN_CHART_WIDTH, chartWidth);
        const { encounterStart, spanMs } = this.getRawEncounterSpan(group);
        const stackedWithAdvisor = !!this.props.stackedWithAdvisor;
        const margin = stackedWithAdvisor ? STACKED_CHART_MARGIN : MARGIN;
        const laneStep = stackedWithAdvisor ? STACKED_LANE_STEP : LANE_STEP;
        const { plotWidthPx, stageWidth } = this.getPlotLayout(viewportWidth, margin);
        const chartLeft = margin.left;
        const { markers, maxLane } = this.getTimelineMarkers(group, plotWidthPx, viewportWidth);
        const trackY = this.computeTrackY(maxLane, margin.top, laneStep, stackedWithAdvisor);
        const tickFontSize = stackedWithAdvisor ? 9 : 10;
        const encounterStartTitleFontSize = stackedWithAdvisor ? 9 : 11;
        const encounterStartLabelY = trackY +
            EventsTimeline.TICK_TIME_LABEL_Y_OFFSET +
            tickFontSize +
            EventsTimeline.GAP_AFTER_TICK_TIME_LABELS;
        const stageHeightRaw = this.computeStageHeightFromTrack(trackY, tickFontSize, encounterStartTitleFontSize, stackedWithAdvisor);
        const minStage = stackedWithAdvisor ? 64 : 160;
        const stageHeight = Math.max(minStage, stageHeightRaw);
        return {
            group,
            onCreateSimulationForBar,
            encounterStart,
            spanMs,
            plotWidthPx,
            stageWidth,
            chartLeft,
            trackY,
            stageHeight,
            markers,
            barMenu,
            creatingSimulation,
            selectedMarkerKey,
            stackedWithAdvisor,
            tickFontSize,
            encounterStartLabelY,
            encounterStartTitleFontSize,
            laneStep,
        };
    };
    //######################### Render #########################
    render_EventTypeIcon = (eventType, iconRest) => {
        const Icon = ENCOUNTER_EVENT_TYPE_ICONS[eventType] ?? ICONS.information.component;
        return _jsx(Icon, { ...iconRest });
    };
    render_EmptyState = (noEventsInEncounter) => {
        return (_jsx("div", { className: 'ehr-emulator__events-timeline__empty', children: noEventsInEncounter ? 'No events in this encounter.' : 'No events to show on the timeline.' }));
    };
    render_TimeTicks = (c) => {
        const tickCount = Math.min(24, Math.max(6, Math.ceil(c.plotWidthPx / 140)));
        const nodes = [];
        for (let i = 0; i <= tickCount; i++) {
            const t = c.encounterStart + (c.spanMs * i) / tickCount;
            const x = c.chartLeft + (c.plotWidthPx * i) / tickCount;
            nodes.push(_jsx(Line, { points: [x, c.trackY - 4, x, c.trackY + 4], stroke: TICK_COLOR, strokeWidth: 1, listening: false }, `tick-${i}`));
            nodes.push(_jsx(Text, { x: x - 32, y: c.trackY + EventsTimeline.TICK_TIME_LABEL_Y_OFFSET, width: 64, align: 'center', text: formatElapsedSinceStart(t - c.encounterStart), fontSize: c.tickFontSize, fill: AXIS_COLOR, listening: false }, `tick-lbl-${i}`));
        }
        return _jsx(React.Fragment, { children: nodes });
    };
    render_MarkerIconsOverlay = (c) => {
        const onCreate = c.onCreateSimulationForBar;
        const encounterKey = c.group.encounterData._id ?? 'encounter';
        return (_jsx("div", { className: 'ehr-emulator__events-timeline__marker-icons-overlay', children: c.markers.map((m, index) => {
                const { x, y } = getMarkerCenterPx(m, c.encounterStart, c.spanMs, c.trackY, c.chartLeft, c.plotWidthPx, c.laneStep);
                const ring = ICON_DRAW_SIZE / 2 + 3;
                const d = ring * 2;
                const tooltipId = `ehr-emulator-timeline-marker-${encounterKey}-${index}`;
                const isSelected = c.selectedMarkerKey === m.key;
                const bubbleClass = 'ehr-emulator__events-timeline__marker-bubble' +
                    (isSelected ? ' ehr-emulator__events-timeline__marker-bubble--selected' : '');
                return (_jsx("div", { className: bubbleClass, style: {
                        left: x - ring,
                        top: y - ring,
                        width: d,
                        height: d,
                        zIndex: 10 + m.lane,
                        cursor: onCreate ? 'pointer' : undefined,
                    }, ...openContent.tooltip.top(tooltipId, () => (_jsxs(_Fragment, { children: [getEncounterEventTypeLabel(m.eventType), _jsx("br", {}), formatTimestamp('DD/MM/YYYY HH:mm:ss', m.timeMs), " \u00B7 +", formatElapsedSinceStart(m.timeMs - c.encounterStart)] })), { offset: 8 }), onClick: onCreate ? (e) => this.handleMarkerBubbleClick(m, e) : undefined, children: this.render_EventTypeIcon(m.eventType, {
                        className: 'ehr-emulator__events-timeline__marker-bubble__icon',
                    }) }, `icon-${m.key}`));
            }) }));
    };
    render_BarMenu = (barMenu, creatingSimulation, onDismiss, onCreateSimulation) => {
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'ehr-emulator__events-timeline__menu-backdrop', onClick: onDismiss }), _jsx("div", { className: 'ehr-emulator__events-timeline__menu', style: { left: barMenu.stageX, top: barMenu.stageY }, onClick: (e) => e.stopPropagation(), children: _jsx(Button, { className: 'ehr-emulator__events-timeline__menu__action', disabled: creatingSimulation, onClick: onCreateSimulation, children: _jsx(ICONS.advisor_v4_sendArrow.component, {}) }) })] }));
    };
    render_TimelineStrip = (c) => {
        const onCreate = c.onCreateSimulationForBar;
        return (_jsx("div", { className: 'ehr-emulator__events-timeline__scroll-strip', style: { width: c.stageWidth, minWidth: c.stageWidth }, children: _jsxs("div", { ref: this.stageWrapRef, className: 'ehr-emulator__events-timeline__stage-wrap', style: { width: c.stageWidth, minHeight: c.stageHeight }, children: [_jsx(Stage, { width: c.stageWidth, height: c.stageHeight, children: _jsxs(Layer, { children: [_jsx(Line, { points: [c.chartLeft, c.trackY, c.chartLeft + c.plotWidthPx, c.trackY], stroke: TRACK_LINE_COLOR, strokeWidth: 4, lineCap: 'round', listening: false }), this.render_TimeTicks(c), _jsx(Text, { x: c.chartLeft, y: c.encounterStartLabelY, text: 'Encounter start', fontSize: c.encounterStartTitleFontSize, fill: AXIS_COLOR, listening: false })] }) }), this.render_MarkerIconsOverlay(c), onCreate &&
                        c.barMenu &&
                        this.render_BarMenu(c.barMenu, c.creatingSimulation, () => this.setState({ barMenu: null }), this.handleBarMenuCreateSimulation)] }) }));
    };
    render_Chart = (c) => {
        const emptyViewportMin = c.stackedWithAdvisor ? STACKED_EMPTY_VIEWPORT_MIN_HEIGHT : 200;
        return (_jsx("div", { className: 'ehr-emulator__events-timeline__chart', style: { width: '100%', minHeight: c.stageHeight }, children: _jsx("div", { ref: this.timelineViewportRef, className: 'ehr-emulator__events-timeline__viewport-scroll', style: { minHeight: c.markers.length === 0 ? emptyViewportMin : c.stageHeight }, children: c.markers.length === 0
                    ? this.render_EmptyState(c.group.eventsWithSimulations.length === 0)
                    : this.render_TimelineStrip(c) }) }));
    };
    render() {
        const c = this.getTimelineRenderContext();
        const rootClass = 'ehr-emulator__events-timeline' +
            (c.stackedWithAdvisor ? ' ehr-emulator__events-timeline--stacked-with-advisor' : '');
        return (_jsx(LL_V_L, { className: rootClass, children: this.render_Chart(c) }));
    }
}
//# sourceMappingURL=EventsTimeline.js.map